import { useEffect, useState } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useWaItem } from '../context/WaItemContext';
import { useLoading } from '../context/LoadingContext';
import { getAiWorkingLanguage } from '../utils/routing-config-manager';
import { WaItemDisplay } from '../components/WaItemDisplay';
import ActionCard from '../components/ActionCard';
import Button from '../components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { WaItem, WA_ITEM_ROLE, MAIN_CLASS } from '../wa-tools/wa-item-class';
import { fetchConfigApi } from '../utils/config-calls';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

export default function ArticleToolsPage() {
  const { t, loadPageTranslations } = useTranslation();
  const { 
    mainWaItem, 
    waItems, 
    loading, 
    initializeMainWaItem, 
    refreshMainWaItem,
    addWaItem,
    removeWaItem,
    setWaItems
  } = useWaItem();
  
  // Hook pour le système de loading et messages
  const { 
    isLoading,
    startLoading, 
    stopLoading, 
    updateLoadingMessage, 
    showSuccess, 
    showError,
    showMessage 
  } = useLoading();
  
  const [waItemGroups, setWaItemGroups] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [aiWorkingLanguage, setAiWorkingLanguage] = useState('FR');

  // Charger la langue de travail au démarrage
  useEffect(() => {
    const loadLanguage = async () => {
      const language = await getAiWorkingLanguage();
      if (language) {
        setAiWorkingLanguage(language);
        debug.log(config, `Langue de travail IA chargée: ${language}`);
      }
    };
    loadLanguage();
  }, []);

  // Charger les traductions pour la page TOOLS
  useEffect(() => {
    loadPageTranslations('TOOLS');
  }, [loadPageTranslations]);

  // Initialiser le waItem Main au chargement de la page
  useEffect(() => {
    initializeMainWaItem();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction pour récupérer les tab_actions
  const fetchTabActions = async () => {
    try {
      const aiLanguage = aiWorkingLanguage;
      debug.log(config, 'ArticleToolsPage: Récupération tab_actions pour la langue:', aiLanguage);

      debug.log(config, 'ArticleToolsPage: Récupération tab_actions pour la langue:', aiLanguage);
      
      const response = await fetchConfigApi(`/api/config/read/tab-actions?ia_language=${aiLanguage}`);
      if (response.success && response.tabActions) {
        debug.log(config, 'ArticleToolsPage: Tab actions récupérées:', response.tabActions.length, 'actions');
        
        // Normaliser les valeurs needs pour correspondre aux constantes WA_ITEM_ROLE
        const normalizedActions = response.tabActions.map(action => ({
          ...action,
          needs: action.needs === 'Nothing' ? WA_ITEM_ROLE.NONE : action.needs
        }));
        
        return normalizedActions;
      } else {
        debug.error(config, 'ArticleToolsPage: Réponse tab_actions invalide', response);
        return null;
      }
    } catch (error) {
      debug.error(config, 'ArticleToolsPage: Erreur lors de la récupération des tab_actions:', error.message);
      return null;
    }
  };

  // Fonction pour créer des WaItems supplémentaires selon les tab_actions
  const createAdditionalWaItems = async (tabActions) => {
    if (!mainWaItem || !mainWaItem.isLoaded || !tabActions) {
      debug.log(config, 'ArticleToolsPage: Conditions non remplies pour créer des WaItems supplémentaires:', {
        mainWaItem: !!mainWaItem,
        isLoaded: mainWaItem?.isLoaded,
        tabActionsLength: tabActions?.length
      });
      return;
    }

    debug.log(config, 'ArticleToolsPage: Début création WaItems supplémentaires:', {
      mainWaItemType: mainWaItem.data.type,
      belTags: mainWaItem.belTags,
      tabActionsCount: tabActions.length
    });

    const newWaItems = [];
    const waItemsMap = new Map(); // Pour mapper les IDs aux WaItems
    const createdIds = new Set(); // Pour éviter les doublons basés sur l'ID

    // Création de l'objet linkedArticles selon l'algorithme
    const linkedArticles = {};
    
    // Si mainWaItem.data.povCharacter
    if (mainWaItem.data.povCharacter) {
      const povKey = "povCharacter" + mainWaItem.data.povCharacter.entityClass;
      linkedArticles[povKey] = [mainWaItem.data.povCharacter.id];
    }
    
    // Traitement des relatedCharacters
    if (mainWaItem.data.relatedCharacters && mainWaItem.data.relatedCharacters.length > 0) {
      for (const character of mainWaItem.data.relatedCharacters) {
        const relatedCharactersKey = "relatedCharacters" + character.entityClass;
        if (!linkedArticles[relatedCharactersKey]) {
          linkedArticles[relatedCharactersKey] = [];
        }
        linkedArticles[relatedCharactersKey].push(character.id);
      }
    }
    
    // Traitement des relatedLocations
    if (mainWaItem.data.relatedLocations && mainWaItem.data.relatedLocations.length > 0) {
      for (const location of mainWaItem.data.relatedLocations) {
        const relatedLocationsKey = "relatedLocations" + location.entityClass;
        if (!linkedArticles[relatedLocationsKey]) {
          linkedArticles[relatedLocationsKey] = [];
        }
        linkedArticles[relatedLocationsKey].push(location.id);
      }
    }
    
    // Traitement des relatedItems
    if (mainWaItem.data.relatedItems && mainWaItem.data.relatedItems.length > 0) {
      for (const item of mainWaItem.data.relatedItems) {
        const relatedItemsKey = "relatedItems" + item.entityClass;
        if (!linkedArticles[relatedItemsKey]) {
          linkedArticles[relatedItemsKey] = [];
        }
        linkedArticles[relatedItemsKey].push(item.id);
      }
    }
    
    // Traitement des relatedArticles
    if (mainWaItem.data.relatedArticles && mainWaItem.data.relatedArticles.length > 0) {
      for (const article of mainWaItem.data.relatedArticles) {
        const relatedArticlesKey = "relatedArticles" + article.entityClass;
        if (!linkedArticles[relatedArticlesKey]) {
          linkedArticles[relatedArticlesKey] = [];
        }
        linkedArticles[relatedArticlesKey].push(article.id);
      }
    }
    
    debug.log(config, 'ArticleToolsPage: linkedArticles créé:', linkedArticles);

    // Parcourir les tab_actions
    for (const action of tabActions) {
      debug.log(config, 'ArticleToolsPage: Analyse action:', {
        function_tag: action.function_tag,
        works_on: action.works_on,
        needs: action.needs,
        tagMatches: mainWaItem.belTags.includes(action.function_tag),
        typeMatches: action.works_on === mainWaItem.data.type
      });

      // Vérifier si function_tag est dans belTags et works_on correspond au type
      if (mainWaItem.belTags.includes(action.function_tag) && 
          action.works_on === mainWaItem.data.type) {
        
        debug.log(config, 'ArticleToolsPage: Action trouvée et compatible:', action);

        // Variable pour stocker le WaItem associé à cette action
        let associatedWaItem = null;
        let targetId = null; // ID qui sera utilisé pour getData

        // Déterminer l'ID cible selon le type d'action
        switch (action.needs) {
          case WA_ITEM_ROLE.PARENT:
            targetId = mainWaItem.data.parent?.id;
            break;
          case WA_ITEM_ROLE.RELATED:
            if (action.link_entity_class && linkedArticles[action.link_entity_class] && linkedArticles[action.link_entity_class].length > 0) {
              targetId = linkedArticles[action.link_entity_class][0];
            }
            break;
          case WA_ITEM_ROLE.WORLDMETA:
            const waWorldString = localStorage.getItem('wa_world');
            if (waWorldString) {
              try {
                const waWorld = JSON.parse(waWorldString);
                targetId = waWorld.id;
              } catch (parseError) {
                debug.error(config, 'ArticleToolsPage: Erreur lors du parsing de wa_world:', parseError.message);
              }
            }
            break;
          case WA_ITEM_ROLE.NONE:
            targetId = mainWaItem.id;
            break;
          case WA_ITEM_ROLE.CHILD:
            // Pas d'ID pour CHILD
            break;
        }

        // Vérifier si on a déjà créé un WaItem avec cet ID
        if (targetId && createdIds.has(targetId)) {
          debug.log(config, 'ArticleToolsPage: WaItem avec ID', targetId, 'déjà créé, utilisation de l\'instance existante');
          // Utiliser le WaItem existant avec cet ID
          associatedWaItem = waItemsMap.get(targetId);
        } else {
          switch (action.needs) {
            case WA_ITEM_ROLE.PARENT:
              try {
                debug.log(config, 'ArticleToolsPage: Création WaItem PARENT');
                associatedWaItem = new WaItem(WA_ITEM_ROLE.PARENT);
                
                // Récupérer les données parent si l'ID existe
                if (targetId) {
                  debug.log(config, 'ArticleToolsPage: Récupération données parent ID:', targetId);
                  await associatedWaItem.getData(targetId, MAIN_CLASS.MANUSCRIPT_PART);
                  debug.log(config, 'ArticleToolsPage: Données parent récupérées avec succès');
                  
                  newWaItems.push(associatedWaItem);
                  waItemsMap.set(targetId, associatedWaItem);
                  createdIds.add(targetId);
                } else {
                  debug.log(config, 'ArticleToolsPage: Aucun ID parent trouvé');
                }
              } catch (error) {
                debug.error(config, 'ArticleToolsPage: Erreur lors de la création du waItem PARENT:', error.message);
              }
              break;

            case WA_ITEM_ROLE.RELATED:
              try {
                debug.log(config, 'ArticleToolsPage: Création WaItem RELATED');
                associatedWaItem = new WaItem(WA_ITEM_ROLE.RELATED);
                
                // Chercher linkedArticles avec action.link_entity_class
                if (targetId) {
                  debug.log(config, 'ArticleToolsPage: Récupération données article ID:', targetId);
                  await associatedWaItem.getData(targetId, MAIN_CLASS.ARTICLE);
                  debug.log(config, 'ArticleToolsPage: Données article récupérées avec succès');
                  
                  newWaItems.push(associatedWaItem);
                  waItemsMap.set(targetId, associatedWaItem);
                  createdIds.add(targetId);
                } else {
                  debug.log(config, 'ArticleToolsPage: Aucun article trouvé pour action.link_entity_class:', action.link_entity_class);
                }
              } catch (error) {
                debug.error(config, 'ArticleToolsPage: Erreur lors de la création du waItem ARTICLE:', error.message);
              }
              break;

            case WA_ITEM_ROLE.WORLDMETA:
              try {
                debug.log(config, 'ArticleToolsPage: Création WaItem WORLDMETA');
                associatedWaItem = new WaItem(WA_ITEM_ROLE.WORLDMETA);
                
                if (targetId) {
                  debug.log(config, 'ArticleToolsPage: Récupération données world ID:', targetId);
                  await associatedWaItem.getData(targetId, MAIN_CLASS.ARTICLE);
                  debug.log(config, 'ArticleToolsPage: Données world récupérées avec succès');
                  
                  newWaItems.push(associatedWaItem);
                  waItemsMap.set(targetId, associatedWaItem);
                  createdIds.add(targetId);
                } else {
                  debug.log(config, 'ArticleToolsPage: Aucun wa_world_id trouvé dans localStorage');
                }
              } catch (error) {
                debug.error(config, 'ArticleToolsPage: Erreur lors de la création du waItem WORLDMETA:', error.message);
              }
              break;

            case WA_ITEM_ROLE.CHILD:
              // Ne rien faire
              debug.log(config, 'ArticleToolsPage: Action CHILD - aucune action requise');
              break;

            case WA_ITEM_ROLE.NONE:
              // Ne rien faire mais utiliser le mainWaItem
              debug.log(config, 'ArticleToolsPage: Action NONE - utilisation du mainWaItem');
              associatedWaItem = mainWaItem;
              break;

            default:
              debug.error(config, 'ArticleToolsPage: Valeur needs non reconnue:', action.needs);
              break;
          }
        }

        // Si aucun WaItem associé n'a été trouvé et needs est NONE, utiliser mainWaItem
        if (!associatedWaItem && action.needs === WA_ITEM_ROLE.NONE) {
          associatedWaItem = mainWaItem;
        }

        // Traitement du paramètre action.link
        let finalAssociatedWaItem = associatedWaItem;
        
        if (action.link) {
          if (action.link.toUpperCase() === MAIN_CLASS.BLOCK.toUpperCase()) {
            debug.log(config, 'ArticleToolsPage: Traitement action.link BLOCK');
            
            // Le waItem créé précédemment est conservé pour affichage mais non lié à l'action
            if (associatedWaItem && associatedWaItem.data) {
              try {
                const blockId = associatedWaItem.data.block.id;
                
                // Vérifier si on a déjà créé un WaItem pour ce block
                if (createdIds.has(blockId)) {
                  debug.log(config, 'ArticleToolsPage: WaItem BLOCK avec ID', blockId, 'déjà créé, utilisation de l\'instance existante');
                  finalAssociatedWaItem = waItemsMap.get(blockId);
                } else {
                  // Créer un nouveau waItem pour le block
                  const blockWaItem = new WaItem(associatedWaItem.role);
                  await blockWaItem.getData(blockId, MAIN_CLASS.BLOCK);
                  finalAssociatedWaItem = blockWaItem;
                  
                  // Ajouter le blockWaItem aux collections
                  newWaItems.push(blockWaItem);
                  waItemsMap.set(blockId, blockWaItem);
                  createdIds.add(blockId);
                  
                  debug.log(config, 'ArticleToolsPage: Nouveau waItem BLOCK créé avec ID:', blockId);
                }
              } catch (error) {
                debug.error(config, 'ArticleToolsPage: Erreur lors de la création du waItem BLOCK:', error.message);
                // En cas d'erreur, utiliser le waItem original
                finalAssociatedWaItem = associatedWaItem;
              }
            }
          } else {
            debug.error(config, 'ArticleToolsPage: Lien depuis WA Item inconnu:', action.link);
          }
        }

        // Associer l'action au WaItem correspondant
        if (finalAssociatedWaItem) {
          debug.log(config, `ArticleToolsPage: Association action ${action.task_name}`, action);
          
          // Initialiser le tableau d'actions s'il n'existe pas
          if (!finalAssociatedWaItem.associatedActions) {
            finalAssociatedWaItem.associatedActions = [];
          }
          
          // Ajouter l'action avec les références nécessaires
          finalAssociatedWaItem.associatedActions.push({
            action,
            associatedWaItem: finalAssociatedWaItem,
            mainWaItem
          });
        }


      }
    }

    debug.log(config, 'ArticleToolsPage: WaItems supplémentaires créés:', newWaItems.length, 'IDs créés:', Array.from(createdIds));
    
    // Créer les groupes organisés pour l'affichage
    const groups = [];
    
    // Ajouter le mainWaItem s'il a des actions associées
    if (mainWaItem.associatedActions && mainWaItem.associatedActions.length > 0) {
      groups.push({
        waItem: mainWaItem,
        actionCards: mainWaItem.associatedActions
      });
      debug.log(config, 'ArticleToolsPage: Groupe mainWaItem ajouté avec', mainWaItem.associatedActions.length, 'actions');
    }
    
    // Ajouter les WaItems supplémentaires avec leurs actions
    newWaItems.forEach(waItem => {
      if (waItem.associatedActions && waItem.associatedActions.length > 0) {
        groups.push({
          waItem: waItem,
          actionCards: waItem.associatedActions
        });
      }
    });
    
    debug.log(config, 'ArticleToolsPage: Groupes créés:', groups.length);
    
    // Ajouter les nouveaux waItems au contexte global
    setWaItems(newWaItems);
    
    setWaItemGroups(groups);
  };

  // Effet pour gérer les WaItems supplémentaires quand mainWaItem change
  useEffect(() => {
    const processMainWaItem = async () => {
      if (!mainWaItem) {
        // Si mainWaItem est supprimé, supprimer les autres instances
        setWaItemGroups([]);
        setWaItems([]);
        return;
      }

      if (mainWaItem.isLoaded) {
        // Une fois le nouveau mainWaItem disponible, récupérer les tab_actions
        const tabActions = await fetchTabActions();
        if (tabActions) {
          // Stocker les informations de debug
          setDebugInfo({
            tabActionsCount: tabActions.length,
            mainWaItemType: mainWaItem.data.type,
            belTags: mainWaItem.belTags,
            aiLanguage: aiWorkingLanguage
          });
          
          await createAdditionalWaItems(tabActions);
        }
      }
    };

    processMainWaItem();
  }, [mainWaItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction pour rafraîchir les données du waItem Main
  const handleRefreshMainData = async () => {
    try {
      startLoading('Lecture données World Anvil', 'info');
      
      console.log('handleRefreshMainData called');
      
      updateLoadingMessage('Récupération des données de l\'article principal...');
      await refreshMainWaItem();
      
      updateLoadingMessage('Analyse des actions disponibles...');
      // Les waItems supplémentaires seront créés automatiquement par l'useEffect
      
      stopLoading();
      showSuccess('Données mises à jour avec succès', true, 3000);
      
    } catch (error) {
      stopLoading();
      showError(`Erreur lors de la mise à jour: ${error.message}`, true, 8000);
      console.error('Erreur dans handleRefreshMainData:', error);
    }
  };

  // Debug: afficher les valeurs actuelles
  console.log('ArticleToolsPage render:', { 
    mainWaItem, 
    loading,
    mainWaItemExists: !!mainWaItem,
    mainWaItemId: mainWaItem?.id,
    mainWaItemIsLoaded: mainWaItem?.isLoaded 
  });

  return (
    <div className="bg-white overflow-hidden">
        {/* Section waItem Main */}
        <div className="mb-8">
          {mainWaItem && (
            <div className="flex justify-between items-stretch mb-4">
              <div className="flex-1 mr-4">
                <WaItemDisplay waItem={mainWaItem} />
              </div>
              <Button
                onClick={handleRefreshMainData}
                disabled={loading || isLoading}
                variant="primary"
                size="lg"
                radius="md"
                className="self-stretch"
              >
                <FontAwesomeIcon icon={faRotateRight} />
              </Button>
            </div>
          )}
          
          {!mainWaItem && (
            <div className="flex justify-between items-stretch">
              <div className="bg-gray-100 p-4 rounded-lg flex-1 mr-4">
                <p className="text-gray-600">Aucun waItem Main disponible</p>
              </div>
              <Button
                onClick={handleRefreshMainData}
                disabled={loading || isLoading}
                variant="primary"
                radius="md"
                className="self-stretch"
              >
                <FontAwesomeIcon icon={faRotateRight} />
              </Button>
            </div>
          )}
        </div>

        {/* Section pour les autres waItems et actions disponibles */}
        <div className="mb-8">         
          {waItemGroups.length > 0 && (
            <div className="space-y-4">
              {waItemGroups.map((group, groupIndex) => (
                <div key={`group-${group.waItem.role}-${groupIndex}`} className="border border-gray-200 rounded-lg p-4">
                  <div className="space-y-4">
                    {/* Affichage du WaItem */}
                    <div>
                      <WaItemDisplay waItem={group.waItem} />
                    </div>
                    
                    {/* Affichage des ActionCards associées */}
                    {group.actionCards.length > 0 && (
                      <div className="space-y-2">
                        {group.actionCards.map((actionCard, actionIndex) => (
                          <div key={`${actionCard.action.task_name}-${actionIndex}`}>
                            <ActionCard 
                              action={actionCard.action} 
                              waItems={{
                                mainWaItem: actionCard.mainWaItem,
                                associatedWaItem: actionCard.associatedWaItem,
                                additionalWaItems: waItemGroups.map(g => g.waItem)
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {waItemGroups.length === 0 && (
            <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
              {mainWaItem ? 
                t('TOOLS', 'NO_ACTIONS_AVAILABLE', 'Aucune action disponible pour cet élément.') :
                t('TOOLS', 'ACTIONS_APPEAR_AFTER_LOAD', 'Les actions apparaîtront une fois le waItem Main chargé.')
              }
            </div>
          )}
        </div>
    </div>
  );
}
